import type { DealForwardData } from '@src/types/OTC';
import { getOtcDealSpreadDisplayValue, getOtcDealUnitNotionalDisplayValue } from '@src/utils/OTC/otcDealDisplayUtils';
import {
  AssetRenderer,
  isOTCForwardSecurity,
  logger,
  PricingModeEnum,
  Side,
  useCurrenciesContext,
  useSecuritiesContext,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { InspectorNumberDisplay } from './InspectorNumberDisplay';
import { InspectorText } from './InspectorText';
import { ForwardPriceDisplay } from './PriceDisplay';
import { PricingModeDisplay } from './PricingModeDisplay';
import { QuantityDisplay } from './QuantityDisplay';
import { InspectorSectionsRenderer } from './shared/InspectorSectionsRenderer';
import type { DisplayOutputItem } from './shared/types';
import { useCounterpartyInformation } from './shared/useCounterpartyInformation';
import { useTimingInformation } from './shared/useTimingInformation';

const useForwardTradeInformation = (instrumentData: DealForwardData): DisplayOutputItem => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const dealSecurity = instrumentData.Symbol ? securitiesBySymbol.get(instrumentData.Symbol) : undefined;
  const resolvedDealSecurity = isOTCForwardSecurity(dealSecurity) ? dealSecurity : undefined;

  if (!resolvedDealSecurity) {
    logger.error(
      new Error(`Failed to get OTC forward deal security for symbol ${instrumentData.Symbol ?? '(missing)'}`),
      {
        extra: {
          dealSecurity,
          resolvedDealSecurity,
        },
      }
    );
    return {
      sectionTitle: 'Deal Information',
      fields: [],
    };
  }

  const { BaseAsset, QuoteAsset, PayoffType, DeliveryAsset, Tenor, SettlementType, Index } =
    resolvedDealSecurity.ProductDetails;

  const unitNotionalDisplay = getOtcDealUnitNotionalDisplayValue(instrumentData, {
    securitiesBySymbol,
    currenciesBySymbol,
  });

  return {
    sectionTitle: 'Deal Information',
    fields: compact([
      {
        fieldName: 'Underlying Asset',
        fieldValue: <AssetRenderer asset={BaseAsset} colorful />,
      },
      {
        fieldName: 'Quote Asset',
        fieldValue: <AssetRenderer asset={QuoteAsset} colorful />,
      },
      {
        fieldName: 'Delivery Asset',
        fieldValue: <AssetRenderer asset={DeliveryAsset} colorful />,
      },
      Tenor != null
        ? {
            fieldName: 'Tenor',
            fieldValue: <InspectorText>{Tenor}</InspectorText>,
          }
        : null,
      {
        fieldName: 'Side',
        fieldValue: <Side side={instrumentData.DealSide}>{instrumentData.DealSide}</Side>,
      },
      {
        fieldName: 'Index',
        fieldValue: <InspectorText>{Index}</InspectorText>,
      },
      {
        fieldName: 'Settlement Type',
        fieldValue: <InspectorText>{SettlementType}</InspectorText>,
      },
      {
        fieldName: 'Quantity',
        fieldValue: <QuantityDisplay instrumentData={instrumentData} />,
      },
      !instrumentData.QuantityAsset
        ? {
            fieldName: 'Unit Notional',
            fieldValue: (
              <InspectorNumberDisplay
                value={unitNotionalDisplay.value}
                currency={unitNotionalDisplay.currency}
                increment={unitNotionalDisplay.increment}
              />
            ),
          }
        : null,
      {
        fieldName: 'Notional',
        fieldValue: (
          <InspectorNumberDisplay
            value={instrumentData.DealInfo.NotionalAmount}
            currency={instrumentData.DealInfo.NotionalAsset}
          />
        ),
      },
      {
        fieldName: 'Payoff Type',
        fieldValue: <InspectorText>{PayoffType}</InspectorText>,
      },
    ]),
  };
};

const useForwardPricingInformation = (instrumentData: DealForwardData): DisplayOutputItem => {
  const pricingMode = instrumentData.DealInfo.PricingMode;
  const spreadDisplay = getOtcDealSpreadDisplayValue(instrumentData);

  return {
    sectionTitle: 'Pricing',
    fields: compact([
      {
        fieldName: 'Pricing Mode',
        fieldValue:
          pricingMode != null ? <PricingModeDisplay pricingMode={pricingMode} /> : <InspectorText>-</InspectorText>,
      },
      pricingMode === PricingModeEnum.SpreadToFixing
        ? {
            fieldName: 'Spread',
            fieldValue: spreadDisplay ? (
              <InspectorNumberDisplay value={spreadDisplay.value} currency={spreadDisplay.currency} />
            ) : (
              <InspectorText>-</InspectorText>
            ),
          }
        : null,
      {
        fieldName: 'Forward Price',
        fieldValue: <ForwardPriceDisplay instrumentData={instrumentData} />,
      },
    ]),
  };
};

interface ForwardInstrumentInspectorSectionsProps {
  instrumentData: DealForwardData;
}

export const ForwardInstrumentInspectorSections = ({ instrumentData }: ForwardInstrumentInspectorSectionsProps) => {
  const tradeInfo = useForwardTradeInformation(instrumentData);
  const pricingInfo = useForwardPricingInformation(instrumentData);
  const counterpartyInfo = useCounterpartyInformation(instrumentData);
  const timingInfo = useTimingInformation(instrumentData);

  const sections = [tradeInfo, pricingInfo, counterpartyInfo, timingInfo];

  return <InspectorSectionsRenderer sections={sections} />;
};
